// photoTone.js — bake a tone treatment into a cover photo.
//
// The floral wrap cover offers three photo treatments: natural Color (default),
// Sepia, and B&W. @react-pdf/renderer cannot apply image filters at render
// time, so the chosen tone must be baked into the pixels BEFORE the image is
// handed to the PDF. Baking once to a data URL and feeding the SAME result to
// both the PDF and the on-screen preview guarantees they match exactly
// (COVER-001).
//
// The sepia/bw math mirrors the approved cover mockups (render_cover.py):
//   luminance g = 0.299R + 0.587G + 0.114B   (ITU-R 601-2, same as PIL)
//   bw:    v = min(238, g * 238/255)          (soft black point, not pure 0-255)
//   sepia: t = g/255; R = t*208+40, G = t*175+26, B = t*120+14  (warm tint)

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    // Only http(s) sources need CORS; data: URLs (from resolveCoverPhotoBase64)
    // are same-origin and never taint the canvas.
    if (/^https?:/i.test(src)) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Returns a JPEG data URL with the tone baked in.
 * - tone 'color' (or falsy src) returns src unchanged — the natural-color path
 *   is byte-identical to no processing at all.
 * - If the canvas is tainted (a cross-origin image the S3 CORS policy blocked),
 *   the original src is returned rather than throwing, so a cover never fails
 *   silently right before a print order.
 */
export async function applyPhotoTone(src, tone) {
  if (!src || tone === 'color') return src;
  let img;
  try {
    img = await loadImage(src);
  } catch (err) {
    console.warn('[photoTone] image load failed, using original:', err);
    return src;
  }
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);
  let imageData;
  try {
    imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  } catch (err) {
    console.warn('[photoTone] canvas tainted, using original:', err);
    return src;
  }
  const p = imageData.data;
  for (let i = 0; i < p.length; i += 4) {
    const g = 0.299 * p[i] + 0.587 * p[i + 1] + 0.114 * p[i + 2];
    if (tone === 'bw') {
      const v = Math.min(238, g * (238 / 255));
      p[i] = p[i + 1] = p[i + 2] = v;
    } else { // sepia
      const t = g / 255;
      p[i] = Math.min(255, t * 208 + 40);
      p[i + 1] = Math.min(255, t * 175 + 26);
      p[i + 2] = Math.min(255, t * 120 + 14);
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/jpeg', 0.92);
}
